(function productFormModule(global) {
  'use strict';

  function createSelectionUrls({ productUrl, sectionId, optionValueIds, currentUrl }) {
    const requestUrl = new URL(productUrl || currentUrl, currentUrl);
    const optionValues = (optionValueIds || []).filter(Boolean).join(',');

    if (sectionId) requestUrl.searchParams.set('section_id', sectionId);
    else requestUrl.searchParams.delete('section_id');

    if (optionValues) requestUrl.searchParams.set('option_values', optionValues);
    else requestUrl.searchParams.delete('option_values');

    const browserUrl = new URL(requestUrl.href);
    browserUrl.searchParams.delete('section_id');

    return {
      requestUrl: requestUrl.href,
      browserUrl: browserUrl.href,
    };
  }

  function focusRestoredControl(control) {
    control?.focus();
  }

  function shouldUpdateBrowserUrl(updateUrlValue) {
    return updateUrlValue !== 'false';
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createSelectionUrls, focusRestoredControl, shouldUpdateBrowserUrl };
  }

  if (!global.document) return;

  const instanceKey = Symbol.for('store-builder.product-form');
  const document = global.document;

  if (global[instanceKey]) {
    global[instanceKey].initialize(document);
    return;
  }

  const productSelector = '[data-product-root]';
  const optionSelector = '[data-variant-option-value]';
  const requests = new Map();

  function elementsWithin(root, selector) {
    const elements = [];
    if (root instanceof global.Element && root.matches(selector)) elements.push(root);
    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
    return elements;
  }

  function initializeRoot(root) {
    if (root.hasAttribute('data-product-enhanced')) return;

    root.setAttribute('data-product-enhanced', '');
    root.querySelectorAll('[data-variant-option-group]').forEach((fieldset) => {
      fieldset.disabled = false;
    });
  }

  function initialize(root = document) {
    elementsWithin(root, productSelector).forEach(initializeRoot);
  }

  function setMessage(root, selector, message) {
    const element = root.querySelector(selector);
    if (!element) return;

    element.textContent = message || '';
    if (element.matches('[data-product-error]')) element.hidden = !message;
  }

  function invalidatePurchaseState(root) {
    const form = root.querySelector('[data-product-form]');
    if (!form) return;

    form.setAttribute('aria-busy', 'true');
    form.setAttribute('data-variant-pending', '');
    form.removeAttribute('data-variant-invalid');

    form.querySelectorAll(
      '[data-product-variant-id], [data-product-submit], [data-quantity-input] input, [data-quantity-input] button',
    ).forEach((control) => {
      control.disabled = true;
    });

    setMessage(root, '[data-product-error]', '');
    setMessage(root, '[data-product-status]', root.dataset.loadingMessage || '');
  }

  function retainUnavailablePurchaseState(root) {
    const form = root.querySelector('[data-product-form]');
    if (!form) return;

    form.setAttribute('aria-busy', 'false');
    form.removeAttribute('data-variant-pending');
    form.setAttribute('data-variant-invalid', '');
    setMessage(root, '[data-product-status]', '');
    setMessage(root, '[data-product-error]', root.dataset.variantLoadError || '');
  }

  function updateAttemptedOptionLabel(input) {
    const optionGroup = input.closest('[data-variant-option-group]');
    const selectedValue = optionGroup?.querySelector('[data-selected-option]');
    if (selectedValue) selectedValue.textContent = input.dataset.optionValueName || '';
  }

  function selectedOptionValueIds(root) {
    return Array.from(root.querySelectorAll(`${optionSelector}:checked`))
      .map((input) => input.dataset.optionValueId)
      .filter(Boolean);
  }

  function findProductRoot(parsedDocument, sectionId) {
    return Array.from(parsedDocument.querySelectorAll(productSelector)).find(
      (candidate) => candidate.dataset.sectionId === sectionId,
    );
  }

  function activateScripts(root) {
    root.querySelectorAll('script').forEach((oldScript) => {
      const script = document.createElement('script');
      Array.from(oldScript.attributes).forEach((attribute) => {
        script.setAttribute(attribute.name, attribute.value);
      });
      script.textContent = oldScript.textContent;
      oldScript.replaceWith(script);
    });
  }

  function initializePaymentButton() {
    try {
      global.Shopify?.PaymentButton?.init?.();
    } catch {
      // Accelerated checkout is optional; the native product form remains available.
    }
  }

  function restoreOptionFocus(root, optionPosition, optionValueId) {
    const matchingOption = Array.from(root.querySelectorAll(optionSelector)).find((input) => {
      const group = input.closest('[data-variant-option-group]');
      return (
        input.dataset.optionValueId === optionValueId &&
        group?.dataset.optionPosition === optionPosition
      );
    });

    const fallback =
      root.querySelector(`${optionSelector}:checked:not(:disabled)`) ||
      root.querySelector(`${optionSelector}:not(:disabled)`) ||
      root.querySelector('[data-product-submit]:not(:disabled)');
    const target = matchingOption || fallback;

    focusRestoredControl(target);
  }

  async function updateSelection(input) {
    const root = input.closest(productSelector);
    if (!root) return;

    updateAttemptedOptionLabel(input);
    requests.get(root)?.abort();

    const controller = new AbortController();
    requests.set(root, controller);
    invalidatePurchaseState(root);

    const sectionId = root.dataset.sectionId;
    const optionValueIds = selectedOptionValueIds(root);
    const optionPosition = input.closest('[data-variant-option-group]')?.dataset.optionPosition || '';
    const optionValueId = input.dataset.optionValueId || '';
    const productUrl = input.dataset.productUrl || root.dataset.productUrl;
    const updateUrlValue = root.dataset.updateUrl;
    const urls = createSelectionUrls({
      productUrl,
      sectionId,
      optionValueIds,
      currentUrl: global.location.href,
    });

    try {
      const response = await global.fetch(urls.requestUrl, {
        method: 'GET',
        headers: { Accept: 'text/html' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Product state request failed with ${response.status}`);

      const responseText = await response.text();
      const parsedDocument = new global.DOMParser().parseFromString(responseText, 'text/html');
      const nextRoot = findProductRoot(parsedDocument, sectionId);

      if (!nextRoot) throw new Error('Product state response did not contain the requested section');
      if (requests.get(root) !== controller) return;

      requests.delete(root);
      root.replaceWith(nextRoot);
      initializeRoot(nextRoot);
      activateScripts(nextRoot);
      initializePaymentButton();
      if (shouldUpdateBrowserUrl(updateUrlValue)) {
        global.history.replaceState({}, '', urls.browserUrl);
      }

      nextRoot.dispatchEvent(
        new global.CustomEvent('product:section:load', {
          bubbles: true,
          detail: { sectionId },
        }),
      );

      global.requestAnimationFrame(() => {
        if (nextRoot.isConnected) restoreOptionFocus(nextRoot, optionPosition, optionValueId);
      });
    } catch (error) {
      if (error.name === 'AbortError') return;
      if (requests.get(root) !== controller) return;

      requests.delete(root);
      retainUnavailablePurchaseState(root);
    }
  }

  function destroyWithin(root) {
    elementsWithin(root, productSelector).forEach((productRoot) => {
      requests.get(productRoot)?.abort();
      requests.delete(productRoot);
      productRoot.removeAttribute('data-product-enhanced');
      productRoot.querySelectorAll('[data-variant-option-group]').forEach((fieldset) => {
        fieldset.disabled = true;
      });
    });
  }

  document.addEventListener('change', (event) => {
    if (!(event.target instanceof global.Element) || !event.target.matches(optionSelector)) return;
    updateSelection(event.target);
  });
  document.addEventListener('product:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  global[instanceKey] = { initialize, destroyWithin };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})(typeof window !== 'undefined' ? window : globalThis);
